import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { client } from "../../api/client"

const apiURL = process.env.REACT_APP_RECORDING_API_URL
const recordCount = parseInt(process.env.REACT_APP_RECORDCOUNT) || 100;

export const fetchRecordingFile = createAsyncThunk(
  'recordings/fetchRecordingFile',
  async (paramaters) => {
    try {
      let jsonParams = JSON.parse(JSON.stringify({key: paramaters}));
      const response = await client.post(apiURL, '/api/v1/retrieve', jsonParams)
      console.log('fetchRecordingFile resp: ', paramaters, response.data)
      return response.data
    } catch (error) {
      console.log('recordings/fetchRecordingFile: ', error)
    }
  })

export const fetchRecordingsBatch = createAsyncThunk(
  'recordings/fetchRecordingsBatch',
  /**
   * initialParams = {
   *   // all your usual search params (DateFrom, UserIds…)
   *   LastEvaluatedKey: {},    // empty for first page, or carryover for next
   *   append: boolean,         // false = replace array, true = append
   * }
   */
  async (initialParams, { rejectWithValue }) => {
    try {

      console.log('⚡ fetchRecordingsBatch triggered with:', initialParams);

      let allCalls = [];
      let params = JSON.parse(JSON.stringify(initialParams));
      const append = !!params.append;
      delete params.append;

      let effectiveLastKey = null;

      while (allCalls.length < recordCount) {
        const resp = await client.post(apiURL, '/api/v1/query', params);
        const { CallsTable = [], LastEvaluatedKey } = resp.data;

        console.log('📦 Response batch:', CallsTable.length, 'LastEvaluatedKey:', LastEvaluatedKey);

        allCalls = allCalls.concat(CallsTable);

        if (LastEvaluatedKey && Object.keys(LastEvaluatedKey).length > 0) {
          params.LastEvaluatedKey = LastEvaluatedKey;
          effectiveLastKey = LastEvaluatedKey;
        } else {
          effectiveLastKey = null;
          break;
        }

        if (allCalls.length >= recordCount) {
          break;
        }
      }

      console.log('✅ Returning', allCalls.length, 'calls. Next key:', effectiveLastKey);
      
      const isExhausted = !effectiveLastKey || Object.keys(effectiveLastKey).length === 0;

      // if exhausted, return everything; otherwise, slice and update LastEvaluatedKey
      const finalCalls = isExhausted ? allCalls : allCalls.slice(0, recordCount);
      const lastDisplayed = finalCalls[finalCalls.length - 1];
      
      // clone and adjust the key only if not exhausted
      let adjustedLastKey = isExhausted
        ? {}
        : { ...effectiveLastKey, StartTime: lastDisplayed?.StartTime };
      
      return {
        CallsTable: finalCalls,
        LastEvaluatedKey: adjustedLastKey,
        append
      };

    }
    catch (err) {
      console.log('❌ recordings/fetchRecordingsBatch: ', err)
      return rejectWithValue(err.message)
    }
  }
)

const recordingsSlice = createSlice({
  name: 'recordings',
  initialState: {
    recordings: [],
    recordingFile: '',
    status: 'idle',
    error: null,
    lastkey: {},
    recordingParams: {},
  },
  reducers: {
    clearData(state) {
      state.recordings = [];
      state.recordingFile = '';
      state.recordingParams = {};
    },
    setRecordingParams(state, action) {
      state.recordingParams = action.payload;
      //console.log('setRecordingParams', state.recordingParams)
    },
    resetRecordingFile(state) {
      state.recordingFile = '';
    }
  },
  extraReducers: builder => builder

    //get recording file and add to recording obj
    .addCase(fetchRecordingFile.pending, (state, action) => {
      state.status = 'loading'
    })
    .addCase(fetchRecordingFile.fulfilled, (state, action) => {
      state.status = 'succeeded'
      //set fetched recording file
      console.log('fetchRecordingFile.fulfilled', action.payload)
      state.recordingFile = action.payload
    })
    .addCase(fetchRecordingFile.rejected, (state, action) => {
      state.status = 'failed'
      console.log('fetchRecordingFile.rejected error: ', action.error.message)
      state.error = action.error.message
    })

    //get recordings batch
    .addCase(fetchRecordingsBatch.pending, state => {
      state.status = 'loading'
    })
    .addCase(fetchRecordingsBatch.fulfilled, (state, { payload }) => {
      state.status = 'succeeded';
      if (payload.append) {
        state.recordings = state.recordings.concat(payload.CallsTable);
      } else {
        state.recordings = payload.CallsTable;
      }

      // Set single source of truth
      state.lastkey = payload.LastEvaluatedKey;

      // Don't touch recordingParams.LastEvaluatedKey inside the slice.
    })
    .addCase(fetchRecordingsBatch.rejected, (state, { payload, error }) => {
      state.status = 'failed'
      state.error = payload || error.message
      console.error('❌ Batch failed:', payload || error.message)
    })
})

export const { clearData, setRecordingParams, resetRecordingFile } = recordingsSlice.actions

export const selectRecordingFile = (state) => state.recordings.recordingFile
export const selectRecordings = (state) => state.recordings.recordings
export const selectLastEvaluatedKey = (state) => state.recordings.lastkey

export default recordingsSlice.reducer